import { type DistanceUnit, distanceUnits } from "@/models/siteUnit.types";
import type {
	ICompactionOutline,
	TOffsetType,
} from "@/store/modules/spreading-compaction-types";
import * as filesaver from "file-saver";
import JSZip from "jszip";
import { buildDxfString } from "./dxfExporter";
import {
	buildSurfaceExportFiles,
	sanitizeSurfaceExportName,
	sortByLayerOrder,
} from "./surfaceExportFiles";

interface IGeometryLike {
	vertices: number[];
	indices: number[];
}

interface IGeometryPart {
	type: TOffsetType;
	offset: number;
	geometry: IGeometryLike;
}

/** Minimum layer digits, aligned with Groupware export for lexical sorting. */
const MIN_LAYER_DIGITS = 2;

function digitsOf(value: number): number {
	return String(Math.max(1, Math.abs(Math.trunc(value)))).length;
}

function pad(value: number, digits: number): string {
	return String(value).padStart(digits, "0");
}

export interface IZipExportOptions {
	/** ファイル名の接頭辞 (`<現場名>_<SpreadingCompaction 名>`) */
	displayName: string;
	parts: ReadonlyArray<IGeometryPart>;
	/** DXF 生成は v2-server の site スコープ route なので必須 */
	siteId: string;
	distanceUnit?: DistanceUnit;
	compactionOutline?: ReadonlyArray<ICompactionOutline>;
}

/**
 * 巻き出し LandXML/DXF と転圧 CSV を ZIP にまとめて保存する。
 * ファイル名・バイト列は Groupware 出力 (`buildSurfaceExportFiles`) と同一。
 */
export async function buildAndSaveZip(
	options: Readonly<IZipExportOptions>,
): Promise<void> {
	const {
		displayName,
		parts,
		siteId,
		distanceUnit = distanceUnits.METERS,
		compactionOutline = [],
	} = options;
	const base = sanitizeSurfaceExportName(displayName);
	const zip = new JSZip();

	const files = buildSurfaceExportFiles(
		displayName,
		parts,
		distanceUnit,
		compactionOutline,
	);
	for (const file of files) {
		const directory = file.fileName.endsWith(".csv")
			? "Compaction"
			: "Spreading/xml";
		zip.file(`${directory}/${file.fileName}`, file.content);
	}

	// XML と同じ番号 (オフセット降順 = 最下層が 01)
	const spreading = sortByLayerOrder(
		parts.filter((p) => p.type === "spreading"),
	);
	const layerDigits = Math.max(MIN_LAYER_DIGITS, digitsOf(spreading.length));
	// DXF is produced by v2-server (ezdxf, R2007); one request per layer
	const dxfFiles = await Promise.all(
		spreading.map(async (part, i) => {
			const projectName = `${base}_layer${pad(i + 1, layerDigits)}`;
			return {
				path: `Spreading/dxf/${projectName}.dxf`,
				content: await buildDxfString(
					part.geometry,
					projectName,
					siteId,
					distanceUnit,
				),
			};
		}),
	);
	for (const file of dxfFiles) {
		zip.file(file.path, file.content);
	}

	const blob = await zip.generateAsync({ type: "blob" });
	filesaver.saveAs(blob, `${base}.zip`);
}
